import { forwardRef, ForwardedRef, useState, ChangeEvent, KeyboardEvent, RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeIcon } from '../../components/icons/eye_icon.component';
import { EyeSlashIcon } from '../../components/icons/eye_slash_icon.component';
import { PulsePin } from '@umanforge/common';

interface InputProp {
  value: string;
  label?: string;
  maxLength?: number;
  onlyUpperCase?: boolean;
  placeholder?: string;
  icon?: React.SVGProps<SVGSVGElement>;
  required?: boolean;
  isPassword?: boolean;
  disabled?: boolean;
  checkContent?: ( text: string ) => string | null;
  onChange?: ( string: string ) => void;
  onKeyEnterUp?: ( event: KeyboardEvent ) => void;
};

export const Input = forwardRef( function Input( { value, maxLength, label, placeholder, icon, checkContent, onChange, onKeyEnterUp, required = false, onlyUpperCase = false, isPassword = false, disabled = false }: InputProp, ref: ForwardedRef<HTMLInputElement> ) {
  const { t } = useTranslation();

  const [ isFocus, setIsFocus ] = useState<boolean>( false );
  const [ isHide, setIsHide ] = useState<boolean>( true );
  const [ inputValue, setInputValue ] = useState<string>( value );
  const [ errorMessage, setErrorMessage ] = useState<string>( '' );

  useEffect( () => {
    if ( ref && checkContent != undefined ) {
      const input = ref as RefObject<HTMLInputElement>;
      if ( input.current ) {
        const error = checkContent( input.current.value );
        setErrorMessage( error ?? '' );
      }
    }
  }, [] );

  return (
    <div className='flex flex-col gap-[2px] w-full'>
      <p className='flex gap-1 ml-1 items-center text-base'>
        {label}
      </p>
      <div className='relative'>
        <div
          className={
            'flex h-9 rounded-lg py-1 outline min-w-[150px] ' +
            ( icon ? 'pr-2 ' : 'px-2 ' ) +
            ( isFocus ? 'outline-2 outline-gray-base-dark bg-off-white ' : 'outline-1 outline-gray-base ' ) +
            ( disabled ? 'bg-stone-100 text-gray-600' : 'hover:outline-gray-base-dark hover:bg-off-white text-black' )
          }
        >
          {
            icon ?
              <div className='flex items-center my-0.5 mx-2 pr-2 select-none border-r'>
                { icon as JSX.Element }
              </div>
              :
              null
          }
          <input
            required={ required }
            disabled={ disabled }
            style={{ outline: 'none' }}
            ref={ ref }
            value={ inputValue }
            className='w-full h-full select-none caret-secondary bg-transparent'
            placeholder={ isPassword && isHide ? '•'.repeat( String( placeholder ).length ) : placeholder }
            type={ isPassword && isHide ? 'password' : 'text' }
            onFocus={ () => { setIsFocus( !disabled && true ); } }
            onBlur={ () => { setIsFocus( false ); } }
            onChange={ ( event: ChangeEvent ) => {
              let result = ( event.target as HTMLInputElement ).value;
              if ( !maxLength || result.length <= maxLength ) {
                if ( inputValue != null ) {
                  if ( onlyUpperCase ) {
                    result = result.toUpperCase();
                  }
                  setInputValue( result );
                }
                if ( onChange != undefined ) {
                  onChange( result );
                }
                if ( checkContent != undefined ) {
                  setErrorMessage( checkContent( ( event.currentTarget as HTMLInputElement ).value ) ?? '' );
                }
              }
            } }
            onKeyUp={ event => {
              if ( event.key === 'Enter' ) {
                if ( onKeyEnterUp != undefined ) {
                  onKeyEnterUp( event );
                  ( event.target as HTMLInputElement ).blur();
                }
              }
            } }
          />
          { isPassword ?
            <div
              className='self-center ml-2'
              onClick={ () => { setIsHide( !isHide ); } }
            >
              {
                isHide ?
                  <EyeIcon className='w-5 h-5 select-none'/>
                  :
                  <EyeSlashIcon className='w-5 h-5 select-none'/>
              }
            </div>
            : null
          }
        </div>
        {
          isFocus && maxLength ?
            <div
              className={
                'absolute h-full ml-1 -top-3.5 z-10 transition ease-in-out delay-75 ' +
                ( required ? 'right-3 ' : 'right-0.5 ' ) +
                ( required && inputValue.length != 0 ? 'translate-x-2.5' : '' )
              }
            >
              <span
                className={
                  'h-fit w-fit px-1 py-0.5 rounded-md text-white text-[10.5px] font-bold ' +
                  ( inputValue.length >= maxLength ? 'bg-red-400 ' : 'bg-gray-400 ' )
                }
              >
                { inputValue.length } / { maxLength }
              </span>
            </div>

            :
            null
        }
        <PulsePin isDisplay={ required && ( ( inputValue == null || inputValue == '' ) || ( checkContent != null && checkContent( inputValue ) != null ) ) } isPulse={ isFocus } />
      </div>
      {
        checkContent !== undefined && errorMessage ?
          <p className='h-[12px] text-[10px] text-error'>
            { t( errorMessage ) }
          </p>
          :
          null
      }
    </div>
  );
} );